import { Input, LabeledContainer, Select } from '@schibsted-svp/react-ui';
import QuestionMark from 'components/ui/QuestionMark';
import config from 'config';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import css from './PublicDisplayLifespan.module.scss';

const { metadataKey: PUBLIC_DISPLAY_METADATA_KEY } = config.publicDisplay.lifespan;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;
const SECONDS_PER_WEEK = SECONDS_PER_DAY * 7;

// max 2 months = 60 days; weeks capped at 8
const UNITS = [
    { label: 'Hours', value: 'hours' as const, maxValue: 60 * 24, secondsPerUnit: SECONDS_PER_HOUR },
    { label: 'Days', value: 'days' as const, maxValue: 60, secondsPerUnit: SECONDS_PER_DAY },
    { label: 'Weeks', value: 'weeks' as const, maxValue: 8, secondsPerUnit: SECONDS_PER_WEEK },
];

type Unit = (typeof UNITS)[number]['value'];

function getUnitConfig(unit: Unit) {
    return UNITS.find((u) => u.value === unit)!;
}

function secondsToDisplay(seconds: number): { amount: number; unit: Unit } {
    if (seconds % SECONDS_PER_WEEK === 0) {
        return { amount: seconds / SECONDS_PER_WEEK, unit: 'weeks' };
    }
    if (seconds % SECONDS_PER_DAY === 0) {
        return { amount: seconds / SECONDS_PER_DAY, unit: 'days' };
    }
    return { amount: seconds / SECONDS_PER_HOUR, unit: 'hours' };
}

function buildAmountSchema(unit: Unit) {
    const { maxValue } = getUnitConfig(unit);
    return z.coerce
        .number({ invalid_type_error: 'Must be a whole number' })
        .int({ message: 'Must be a whole number' })
        .positive({ message: 'Must be a whole number' })
        .max(maxValue, { message: `Max value is ${maxValue} ${unit}` });
}

function validate(amount: string, unit: Unit): string | null {
    if (!amount) {
        return null;
    }
    const result = buildAmountSchema(unit).safeParse(amount);
    if (result.success === false) {
        return result.error.issues[0].message;
    }
    return null;
}

function parseExistingValue(existingValue: string | null): { amount: string; unit: Unit } {
    const result = z.coerce.number().positive().safeParse(existingValue);
    if (result.success) {
        const { amount, unit } = secondsToDisplay(result.data);
        return { amount: String(amount), unit };
    }
    return { amount: '', unit: 'days' };
}

type SimpleMetadataEntry = { key: string; value: string };

type PublicDisplayLifespanProps = {
    fields: (SimpleMetadataEntry & { id: string })[];
    update: (index: number, value: SimpleMetadataEntry) => void;
    append: (value: SimpleMetadataEntry) => void;
    remove: (index: number) => void;
};

export function PublicDisplayLifespan({ fields, update, append, remove }: PublicDisplayLifespanProps) {
    const existingIndex = fields.findIndex(({ key }) => key === PUBLIC_DISPLAY_METADATA_KEY);
    const existingValue = existingIndex >= 0 ? fields[existingIndex].value : null;

    const [{ amount, unit }, setValues] = useState(() => parseExistingValue(existingValue));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const parsed = parseExistingValue(existingValue);
        if (parsed.amount) {
            setValues(parsed);
            setError(null);
        }
    }, [existingValue]);

    const saveToMetadata = (newAmount: string, newUnit: Unit) => {
        const result = buildAmountSchema(newUnit).safeParse(newAmount);
        if (result.success === false) {
            if (existingIndex >= 0) {
                remove(existingIndex);
            }
            return;
        }
        const entry = {
            key: PUBLIC_DISPLAY_METADATA_KEY,
            value: String(result.data * getUnitConfig(newUnit).secondsPerUnit),
        };
        if (existingIndex >= 0) {
            update(existingIndex, entry);
        } else {
            append(entry);
        }
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newAmount = e.target.value;
        setValues((prev) => ({ ...prev, amount: newAmount }));
        setError(validate(newAmount, unit));
    };

    const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newAmount = e.target.value;
        const validationError = validate(newAmount, unit);
        setError(validationError);
        if (!validationError) {
            saveToMetadata(newAmount, unit);
        }
    };

    const handleUnitChange = (option: { value: Unit } | null) => {
        const newUnit = option?.value ?? 'days';
        setValues((prev) => ({ ...prev, unit: newUnit }));
        const validationError = validate(amount, newUnit);
        setError(validationError);
        if (!validationError) {
            saveToMetadata(amount, newUnit);
        }
    };

    return (
        <LabeledContainer
            label={
                <>
                    Public Display lifespan
                    <QuestionMark content="How long an asset remains visible in Public Display after publishing. Only used by Public Display. Max 2 months." />
                </>
            }
        >
            <div className={css.inputs}>
                <div className={css.numberInputWrapper}>
                    <Input
                        type="text"
                        inputMode="numeric"
                        size="small"
                        value={amount}
                        onChange={handleAmountChange}
                        onBlur={handleAmountBlur}
                        className={css.numberInput}
                        placeholder="e.g. 7"
                        error={error}
                    />
                </div>
                <Select
                    options={UNITS}
                    value={UNITS.find((u) => u.value === unit) ?? UNITS[1]}
                    onChange={handleUnitChange}
                    size="small"
                    width={120}
                />
            </div>
        </LabeledContainer>
    );
}
